import { Injectable, OnDestroy } from '@angular/core';
import {
  MerchantAppMissingFields,
  MerchantApplicationStepType,
  PaysafeApplicationSteps,
  TsysApplicationSteps,
} from 'app/core/data/onboarding-types';
import { AccountAppService } from 'app/core/services/account.app.service';
import { FilesAppService } from 'app/core/services/files.app.service';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AccountInternalMetadata,
  BankVerification,
  GetOnboardingApplicationRequestParams,
  InternalAccount,
  InternalAccountCapability,
  InternalService,
  ModelFile,
  OnboardingApplication,
  OnboardingApplicationCreateParams,
  OnboardingLegalEntity,
  OnboardingService,
  PricingTemplate,
  ProcessingVolume,
  SigningUrl,
  UpdateOnboardingApplicationRequestParams,
} from '../../../../projects/tilled-api-client/src';
import {
  GetPlaidLinkTokenRequestParams,
  PlaidService,
  PlaidUpdateMerchantApplicationRequestParams,
} from '../../../../projects/tilled-api-client/src/api/plaid.service';
import { ApplicationStep, IsStepComplete } from '../../core/models/application-step';
import { AuthService } from '../../core/services/auth.service';
import { TilledAlert } from '../models/tilled-alert';
import { AlertService } from './alert.service';
import { PlaidConfig, PlaidEventsConfig, PlaidLinkAppService } from './plaid-link.app.service';

@Injectable({
  providedIn: 'root',
})
export class MerchantAppService implements OnDestroy {
  private stepsSubject = new BehaviorSubject<ApplicationStep[]>(null);
  private currentStepSubject = new BehaviorSubject<ApplicationStep>(null);
  private applicationResponse = new BehaviorSubject<OnboardingApplication>(null);
  private submittedApplicationResponse = new Subject<SigningUrl>();
  public merchantAppSteps$: Observable<ApplicationStep[]> = this.stepsSubject.asObservable();
  public merchantApplicationResponse$: Observable<OnboardingApplication> = this.applicationResponse.asObservable();
  //.pipe(skipWhile((value, index) => !value));
  public currentStep$: Observable<ApplicationStep> = this.currentStepSubject.asObservable();
  public submittedApplicationResponse$: Observable<SigningUrl> = this.submittedApplicationResponse.asObservable();

  private submittedApplicationErrors = new BehaviorSubject<any>(null);
  public submittedApplicationErrors$: Observable<any> = this.submittedApplicationErrors.asObservable();

  private _applicationUpdatedAt$ = new BehaviorSubject<string>(null);
  public applicationUpdatedAt$: Observable<any> = this._applicationUpdatedAt$.asObservable();
  private _applicationSuccessfullyUpdatedByPlaid = new Subject<boolean>();
  public applicationSuccessfullyUpdatedByPlaid$: Observable<boolean> =
    this._applicationSuccessfullyUpdatedByPlaid.asObservable();

  private hasBankVerificationEnabled: boolean = false;
  private bankVerificationMethods: BankVerification.VerificationMethodEnum[] = [];
  private areBankDocsRequired: boolean = false;
  private requiredBankDocs: string[] = ['Voided Check', 'Bank Letter'];
  private currentBankDocs: string[] = [];

  private unsubscribe$ = new Subject<void>();

  private _hasTsysProvider$ = new BehaviorSubject<boolean>(false);
  public hasTsysProvider$: Observable<boolean> = this._hasTsysProvider$.asObservable();

  constructor(
    private _merchantApplicationService: OnboardingService,
    private _internalService: InternalService,
    private _plaidService: PlaidService,
    private _alertService: AlertService,
    private _plaidLinkAppService: PlaidLinkAppService,
    private _filesAppService: FilesAppService,
    private _accountService: AccountAppService,
    private _authService: AuthService,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public updateCurrentStep(step: number): void {
    const allSteps = this.stepsSubject.getValue();

    let currentStep = allSteps.filter((s) => s.order === step)[0];
    if (currentStep.subSteps?.length > 0) {
      currentStep = allSteps.filter((step) => step.order === currentStep.subSteps[0])[0];
    }
    this.currentStepSubject.next(currentStep);
  }

  public getLinkToken(eventListeners: PlaidEventsConfig, redirectUri: string): void {
    const params: GetPlaidLinkTokenRequestParams = {
      tilledAccount: AuthService.getCurrentAccountId(),
      redirect: redirectUri,
    };
    this._plaidService.getPlaidLinkToken(params).subscribe({
      next: (response) => {
        const linkScript: any = document.createElement('script');
        linkScript.type = 'text/javascript';
        linkScript.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
        linkScript.onerror = (e: any) => console.log(e);

        const plaidConfig: PlaidConfig = {
          token: response.token,
          ...eventListeners,
        };
        this._plaidLinkAppService.createPlaid(plaidConfig).then((linkHandler) => {
          linkHandler?.open();
        });
        return;
      },
      error: (err) => {
        // generic catch all for error responses
        const message: TilledAlert = {
          message: 'Could not load Plaid interface.',
          title: 'Server error',
          type: 'error',
        };
        this._alertService.showAlert(message);
        this._applicationSuccessfullyUpdatedByPlaid.next(false);
      },
    });
  }

  public async updatePlaidAccessToken(token: string): Promise<void> {
    const params: PlaidUpdateMerchantApplicationRequestParams = {
      token: token,
      tilledAccount: AuthService.getCurrentAccountId(),
    };
    this._plaidService.plaidUpdateMerchantApplication(params).subscribe({
      next: (response) => {
        this.applicationResponse.next(response);
        this._applicationSuccessfullyUpdatedByPlaid.next(true);
      },
      error: (err) => {
        // generic catch all for error responses
        const message: TilledAlert = {
          message: 'Could not update application.',
          title: 'Server error',
          type: 'error',
        };
        this._alertService.showAlert(message);
        this._applicationSuccessfullyUpdatedByPlaid.next(false);
      },
    });
  }

  public getApplication(
    internalMetadata: AccountInternalMetadata,
    accountId?: string,
  ): Observable<OnboardingApplication> {
    const accountIdToUse = accountId ?? AuthService.getCurrentAccountId();
    const params: GetOnboardingApplicationRequestParams = {
      tilledAccount: accountIdToUse,
    };

    this.getUploadedBankDocs(accountIdToUse);

    return this._merchantApplicationService.getOnboardingApplication(params).pipe(
      tap((application) => {
        if (!application.legal_entity) {
          application.legal_entity = {} as OnboardingLegalEntity;
        }
        this.updateProviderData();
        this.loadSteps(application);
        this.updateBankDocsRequired(application, internalMetadata);
        this.applicationResponse.next(application);
        this._applicationUpdatedAt$.next(application.updated_at);
      }),
      catchError((err) => {
        throw JSON.stringify(err);
      }),
    );
  }

  public updateMerchantApplication(updatedApp: OnboardingApplication, nextStep: number, accountId?: string): void {
    const params: UpdateOnboardingApplicationRequestParams = {
      tilledAccount: accountId ?? AuthService.getCurrentAccountId(),
      onboardingApplicationCreateParams: this.mapResponseToParams(updatedApp),
    };
    this.checkAndUpdateFromPricingTemplate(updatedApp);
    this.updateProviderData();
    this._merchantApplicationService
      .updateOnboardingApplication(params)
      .pipe(
        tap((application) => {
          this.loadSteps(application);
          this.currentStepSubject.next(this.stepsSubject.getValue().filter((s) => s.order === nextStep)[0]);
          this._applicationUpdatedAt$.next(application.updated_at);
        }),
        catchError((err) => {
          if (err.error) {
            if (err.error.statusCode === 400) {
              const message: TilledAlert = {
                message: err.error.message,
                title: 'Server error',
                type: 'error',
              };
              this._alertService.showAlert(message);
            } else {
              // generic catch all for error responses
              const message: TilledAlert = {
                message: 'Could not update application, please try again or contact support for assistance',
                title: 'Server error',
                type: 'error',
              };
              this._alertService.showAlert(message);
            }
          }
          throw new Error('Error loading application: ' + JSON.stringify(err));
        }),
      )
      .subscribe((response) => this.applicationResponse.next(response));
  }

  public updateAndSubmitMerchantApplication(updatedApp: OnboardingApplication, accountId?: string): void {
    const params: UpdateOnboardingApplicationRequestParams = {
      tilledAccount: accountId ?? AuthService.getCurrentAccountId(),
      onboardingApplicationCreateParams: this.mapResponseToParams(updatedApp),
    };
    this._merchantApplicationService.updateOnboardingApplication(params).subscribe({
      next: (response) => {
        this.applicationResponse.next(response);
        this.submitMerchantApplication(accountId);
      },
      error: (err) => {
        // generic catch all for error responses
        const message: TilledAlert = {
          message: 'Could not update application, please try again or contact support for assistance',
          title: 'Server error',
          type: 'error',
        };
        this._alertService.showAlert(message);
      },
    });
  }

  public isBusinessDetailsStepComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };

    if (!application.legal_entity.legal_name) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.LEGAL_NAME);
    }
    if (!application.legal_entity.dba_name) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.DBA);
    }
    if (!application.legal_entity.structure) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.ENTITY_TYPE);
    }
    if (!application.legal_entity.tax_id_number) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.BUSINESS_ID);
    }
    if (!application.legal_entity.mcc) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.INDUSTRY);
    }
    if (!application.legal_entity.statement_descriptor) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.STATEMENT_DESCRIPTOR);
    }
    if (!application.legal_entity.product_description) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.PRODUCT_DESCRIPTION);
    }
    if (!application.legal_entity.date_of_incorporation) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.DATE_OF_INC);
    }
    if (
      !(
        application.legal_entity.percent_business_to_business >= 0 &&
        application.legal_entity.percent_business_to_business <= 100
      )
    ) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDetailsStep.PERCENT_B2B);
    }
    return stepComplete;
  }

  public isBusinessContactInformationComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };

    if (!application.legal_entity.support_phone) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.PHONE);
    }
    if (!application.legal_entity.support_email) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.EMAIL);
    }
    // TBD
    /*if (!application.legal_entity.website) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.WEBSITE);
    }*/
    if (!application.legal_entity.address?.street) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.STREET);
    }
    if (!application.legal_entity.address?.city) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.CITY);
    }
    if (!application.legal_entity.address?.state) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.STATE);
    }
    if (!application.legal_entity.address?.postal_code) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.ContactInfoStep.POSTAL);
    }

    return stepComplete;
  }

  public isPaymentProcessingVolumeComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };
    if (!application.legal_entity?.processing_volume?.monthly_transaction_count) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.VolumesStep.AVERAGE_TRX_PER_MONTH);
    }
    if (!application.legal_entity?.processing_volume?.monthly_processing_volume) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.VolumesStep.MONTHLY_VOLUME);
    }

    const cardPricing = application?.pricing_templates
      ? application.pricing_templates.find((p) => p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.CARD)
      : null;
    const debitPricing = application?.pricing_templates
      ? application.pricing_templates.find(
          (p) =>
            p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.ACH_DEBIT ||
            p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.EFT_DEBIT,
        )
      : null;
    if (!application.legal_entity?.processing_volume?.average_transaction_amount_card && cardPricing) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.VolumesStep.AVERAGE_AMOUNT_CARD);
    }
    if (!application.legal_entity?.processing_volume?.average_transaction_amount_debit && debitPricing) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.VolumesStep.AVERAGE_AMOUNT_DEBIT);
    }

    if (
      application.legal_entity?.card_checkout_method_breakdown?.percent_e_commerce +
        application.legal_entity?.card_checkout_method_breakdown?.percent_manual_card_not_present +
        application.legal_entity?.card_checkout_method_breakdown?.percent_swiped !==
      100
    ) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.VolumesStep.TRX_PERCENTS);
    }
    return stepComplete;
  }

  public isBusinessRepresentativesComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };

    if (!application.legal_entity?.principals) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.RepresentativesStep.NO_PRINCIPALS);
      return stepComplete;
    }

    let ssnRequired = true;
    if (this._hasTsysProvider$.getValue()) {
      if (application.legal_entity?.structure === OnboardingLegalEntity.StructureEnum.GOVERNMENT) {
        ssnRequired = false;
      }
    } else {
      if (
        application.legal_entity?.structure === OnboardingLegalEntity.StructureEnum.CHARITY ||
        application.legal_entity?.structure === OnboardingLegalEntity.StructureEnum.GOVERNMENT ||
        application.legal_entity?.structure === OnboardingLegalEntity.StructureEnum.NON_PROFIT
      ) {
        ssnRequired = false;
      }
    }

    for (const [index, principal] of application.legal_entity?.principals?.entries()) {
      if (
        !(
          application.pricing_templates[0]?.currency === ProcessingVolume.CurrencyEnum.CAD ||
          !ssnRequired ||
          principal.id_number
        )
      ) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.SSN,
        );
      }
      if (!principal.first_name) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.FIRST_NAME,
        );
      }
      if (!principal.last_name) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.LAST_NAME,
        );
      }
      if (!principal.phone) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.PHONE,
        );
      }
      if (!principal.date_of_birth) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.DOB,
        );
      }
      if (!principal.job_title) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.JOB_TITLE,
        );
      }
      if (principal.percent_ownership == null) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.OWNERSHIP,
        );
      }
      if (!principal.address?.street) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.STREET,
        );
      }
      if (!principal.address?.city) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.CITY,
        );
      }
      if (!principal.address?.state) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.STATE,
        );
      }
      if (!principal.address?.postal_code) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.POSTAL,
        );
      }

      const needPreviousAddress =
        application.pricing_templates[0]?.currency === ProcessingVolume.CurrencyEnum.CAD &&
        principal.years_at_address <= 3;
      if (needPreviousAddress && !principal.previous_address?.street) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.PREV_STREET,
        );
      }
      if (needPreviousAddress && !principal.previous_address?.city) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.PREV_CITY,
        );
      }
      if (needPreviousAddress && !principal.previous_address?.state) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.PREV_STATE,
        );
      }
      if (needPreviousAddress && !principal.previous_address?.postal_code) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.PREV_POSTAL,
        );
      }
      if (this._hasTsysProvider$.getValue() && principal.is_control_prong && !principal.email) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(
          'Business Representative ' + (index + 1) + ' ' + MerchantAppMissingFields.RepresentativesStep.EMAIL,
        );
      }
    }

    const applicants = application.legal_entity.principals.filter((principal) => principal.is_applicant === true);
    if (applicants.length !== 1) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.RepresentativesStep.APPLICANT);
    }
    const controlProngs = application.legal_entity.principals.filter(
      (principal) => principal.is_control_prong === true,
    );
    if (controlProngs.length !== 1) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.RepresentativesStep.CONTROL_PRONG);
    }
    const signatories = application.legal_entity.principals.filter((principal) => principal.is_signatory === true);
    if (signatories.length !== 1) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.RepresentativesStep.SIGNATORY);
    }

    return stepComplete;
  }

  public isBankingInformationComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };

    this.updateBankDocsRequired(application);
    let docRequirementsMet = !this.areBankDocsRequired;
    if (!docRequirementsMet) {
      for (const currentBankDoc of this.currentBankDocs) {
        if (this.requiredBankDocs.includes(currentBankDoc)) {
          docRequirementsMet = true;
          break;
        }
      }
    }

    if (!application.legal_entity?.bank_account?.account_number) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.ACCOUNT_NUMBER);
    }
    if (!application.legal_entity?.bank_account?.routing_number) {
      stepComplete.complete = false;
      if (application.pricing_templates[0]?.currency === ProcessingVolume.CurrencyEnum.CAD) {
        stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.ROUTING_NUMBER_CA);
      } else {
        stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.ROUTING_NUMBER);
      }
    }
    if (!application.legal_entity?.bank_account?.type) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.TYPE);
    }
    if (!application.legal_entity?.bank_account?.account_holder_name) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.ACCOUNT_HOLDER_NAME);
    }
    if (!application.legal_entity?.bank_account?.bank_name) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.BANK_NAME);
    }
    if (!docRequirementsMet) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BankAccountStep.DOC_UPLOAD);
    }

    return stepComplete;
  }

  public isReviewPricingAndTermsComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: application.tos_acceptance,
      missingFields: [],
    };
    return stepComplete;
  }

  public isBusinessDocumentsComplete(application: OnboardingApplication): IsStepComplete {
    const stepComplete: IsStepComplete = {
      complete: true,
      missingFields: [],
    };

    if (!this._hasTsysProvider$.getValue()) {
      return stepComplete;
    }

    if (!application.legal_entity.patriot_act_details) {
      stepComplete.complete = false;
      stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BUSINESS_DOCS);
    } else {
      if (
        !application.legal_entity.patriot_act_details.articles_of_incorporation &&
        !application.legal_entity.patriot_act_details.business_license
      ) {
        stepComplete.complete = false;
        stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BUSINESS_DOCS);
      } else if (application.legal_entity.patriot_act_details.articles_of_incorporation) {
        if (!application.legal_entity.patriot_act_details.articles_of_incorporation.document_id) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.AOI_DOCUMENT);
        }
        if (!application.legal_entity.patriot_act_details.articles_of_incorporation.issued_at) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.AOI_ISSUED_AT);
        }
        if (!application.legal_entity.patriot_act_details.articles_of_incorporation.state) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.AOI_STATE);
        }
      } else if (application.legal_entity.patriot_act_details.business_license) {
        if (!application.legal_entity.patriot_act_details.business_license.document_id) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BL_DOCUMENT);
        }
        if (!application.legal_entity.patriot_act_details.business_license.expires_at) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BL_EXPIRES_AT);
        }
        if (!application.legal_entity.patriot_act_details.business_license.issued_at) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BL_ISSUED_AT);
        }
        if (!application.legal_entity.patriot_act_details.business_license.state) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BL_STATE);
        }
        if (!application.legal_entity.patriot_act_details.business_license.name) {
          stepComplete.complete = false;
          stepComplete.missingFields.push(MerchantAppMissingFields.BusinessDocumentsStep.BL_NAME);
        }
      }
    }

    return stepComplete;
  }

  public updateBankDocsRequiredFromComponent(required: boolean, current?: string[]): void {
    this.areBankDocsRequired = required;
    if (current && current.length > 0) {
      this.currentBankDocs = current;
    }
  }

  private getUploadedBankDocs(accountId: string): void {
    this._filesAppService.filesAll$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (files) => {
        if (files) {
          this.currentBankDocs = [];
          for (const file of files) {
            this.currentBankDocs.push(file.title?.split(':')[0]);
          }
        }
        return;
      },
      error: (err) => {
        if (this.areBankDocsRequired) {
          // generic catch all for error responses
          const message: TilledAlert = {
            message: 'Could not load Files.',
            title: 'Server error',
            type: 'error',
          };
          this._alertService.showAlert(message);
        }
      },
    });
    this._filesAppService.listAllFiles(accountId, [ModelFile.PurposeEnum.ONBOARDING_DOCUMENTATION]);
  }

  private submitMerchantApplication(accountId?: string): void {
    this._merchantApplicationService
      .submitOnboardingApplication({ tilledAccount: accountId ?? AuthService.getCurrentAccountId() })
      .subscribe({
        next: (response) => {
          this.submittedApplicationResponse.next(response);
        },
        error: (err) => {
          this.submittedApplicationErrors.next(err);
        },
      });
  }

  private checkAndUpdateFromPricingTemplate(application: OnboardingApplication): void {
    const cardPricing = application.pricing_templates.find(
      (p) => p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.CARD,
    );
    const debitPricing = application.pricing_templates.find(
      (p) =>
        p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.ACH_DEBIT ||
        p?.payment_method_type === PricingTemplate.PaymentMethodTypeEnum.EFT_DEBIT,
    );

    if (application.legal_entity) {
      if (!application.legal_entity.processing_volume) {
        application.legal_entity.processing_volume = {} as ProcessingVolume;
      }
      const validPricingTemplate = cardPricing || debitPricing;
      if (validPricingTemplate?.currency === PricingTemplate.CurrencyEnum.USD) {
        application.legal_entity.processing_volume.currency = ProcessingVolume.CurrencyEnum.USD;
        application.legal_entity.region = OnboardingLegalEntity.RegionEnum.US;
      } else if (validPricingTemplate?.currency === PricingTemplate.CurrencyEnum.CAD) {
        application.legal_entity.processing_volume.currency = ProcessingVolume.CurrencyEnum.CAD;
        application.legal_entity.region = OnboardingLegalEntity.RegionEnum.CA;
      } else {
        application.legal_entity.processing_volume.currency = ProcessingVolume.CurrencyEnum.USD;
        application.legal_entity.region = OnboardingLegalEntity.RegionEnum.US;
      }
    }
  }

  private loadSteps(application: OnboardingApplication): void {
    const steps = new Array<ApplicationStep>();

    // Paysafe Application
    if (!this._hasTsysProvider$.getValue()) {
      steps.push(
        {
          order: PaysafeApplicationSteps.BUSINESS_DETAILS_STEP,
          type: MerchantApplicationStepType.BUSINESS_DETAILS,
          title: 'Business Details',
          icon: 'format_align_left',
          complete: this.isBusinessDetailsStepComplete(application),
        },
        {
          order: PaysafeApplicationSteps.BUSINESS_CONTACT_STEP,
          type: MerchantApplicationStepType.BUSINESS_CONTACT,
          title: 'Contact Info',
          icon: 'phone',
          complete: this.isBusinessContactInformationComplete(application),
        },
        {
          order: PaysafeApplicationSteps.PROCESSING_VOLUMES_STEP,
          type: MerchantApplicationStepType.PROCESSING_VOLUMES,
          title: 'Processing Volumes',
          icon: 'credit_card',
          complete: this.isPaymentProcessingVolumeComplete(application),
        },
        {
          order: PaysafeApplicationSteps.REPRESENTATIVES_STEP,
          type: MerchantApplicationStepType.REPRESENTATIVES,
          title: 'Representatives',
          icon: 'badge',
          complete: this.isBusinessRepresentativesComplete(application),
        },
        {
          order: PaysafeApplicationSteps.BANK_ACCOUNT_STEP,
          type: MerchantApplicationStepType.BANK_ACCOUNT,
          title: 'Bank Account',
          icon: 'account_balance',
          complete: this.isBankingInformationComplete(application),
        },
        {
          order: PaysafeApplicationSteps.SUBMIT_STEP,
          type: MerchantApplicationStepType.SUBMIT,
          title: 'Submit Application',
          icon: 'check',
          complete: this.isReviewPricingAndTermsComplete(application),
        },
      );
    } else {
      steps.push(
        {
          order: TsysApplicationSteps.BUSINESS_DETAILS_STEP,
          type: MerchantApplicationStepType.BUSINESS_DETAILS,
          title: 'Business Details',
          icon: 'format_align_left',
          complete: this.isBusinessDetailsStepComplete(application),
        },
        {
          order: TsysApplicationSteps.BUSINESS_CONTACT_STEP,
          type: MerchantApplicationStepType.BUSINESS_CONTACT,
          title: 'Contact Info',
          icon: 'phone',
          complete: this.isBusinessContactInformationComplete(application),
        },
        {
          order: TsysApplicationSteps.PROCESSING_VOLUMES_STEP,
          type: MerchantApplicationStepType.PROCESSING_VOLUMES,
          title: 'Processing Volumes',
          icon: 'credit_card',
          complete: this.isPaymentProcessingVolumeComplete(application),
        },
        {
          order: TsysApplicationSteps.REPRESENTATIVES_STEP,
          type: MerchantApplicationStepType.REPRESENTATIVES,
          title: 'Representatives',
          icon: 'badge',
          complete: this.isBusinessRepresentativesComplete(application),
        },
        {
          order: TsysApplicationSteps.BANK_ACCOUNT_STEP,
          type: MerchantApplicationStepType.BANK_ACCOUNT,
          title: 'Bank Account',
          icon: 'account_balance',
          complete: this.isBankingInformationComplete(application),
        },
        {
          order: TsysApplicationSteps.BUSINESS_DOCUMENTS_STEP,
          type: MerchantApplicationStepType.BUSINESS_DOCUMENTS,
          title: 'Business Documents',
          icon: 'drive_folder_upload',
          complete: this.isBusinessDocumentsComplete(application),
        },
        {
          order: TsysApplicationSteps.SUBMIT_STEP,
          type: MerchantApplicationStepType.SUBMIT,
          title: 'Submit Application',
          icon: 'check',
          complete: this.isReviewPricingAndTermsComplete(application),
        },
      );
    }

    this.stepsSubject.next(steps);
    let currentStep = steps[0];
    if (currentStep.subSteps?.length > 0) {
      currentStep = steps.filter((step) => step.order === currentStep.subSteps[0])[0];
    }
    this.currentStepSubject.next(currentStep);
  }

  private mapResponseToParams(response: OnboardingApplication): OnboardingApplicationCreateParams {
    const params: OnboardingApplicationCreateParams = {
      tos_acceptance: response.tos_acceptance,
      legal_entity: response.legal_entity,
    };

    return params;
  }

  private updateBankDocsRequired(application: OnboardingApplication, internalMetadata?: AccountInternalMetadata): void {
    if (internalMetadata) {
      this.bankVerificationMethods = internalMetadata?.bank_verification_methods;
      this.hasBankVerificationEnabled =
        this.bankVerificationMethods?.includes(BankVerification.VerificationMethodEnum.PLAID) ||
        this.bankVerificationMethods?.includes(BankVerification.VerificationMethodEnum.TILLED_MANUAL)
          ? true
          : false;
    }
    if (this.hasBankVerificationEnabled) {
      const accNum = application.legal_entity?.bank_account?.account_number;
      if (
        (accNum == null || accNum.trim().length === 0) &&
        this.bankVerificationMethods?.includes(BankVerification.VerificationMethodEnum.PLAID)
      ) {
        this.areBankDocsRequired = false;
      } else {
        if (application.bank_verification?.verification_status === BankVerification.VerificationStatusEnum.VERIFIED) {
          this.areBankDocsRequired = false;
        } else {
          this.areBankDocsRequired = true;
        }
      }
    } else {
      this.areBankDocsRequired = false;
    }
  }

  public getMccDescriptions(accountId: string): Observable<any[]> {
    return this._merchantApplicationService.getMccDescriptions({ tilledAccount: accountId });
  }

  public updateProviderData(): void {
    combineLatest([
      this._authService.account$,
      this._accountService.connectedAccount$.pipe(
        takeUntil(this.unsubscribe$),
        catchError(() => EMPTY),
      ),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        map(([account, connectedAccount]) => connectedAccount || account),
        filter((account) => !!account),
        switchMap((account: InternalAccount) => {
          let hasTsys = false;
          for (const capability of account.capabilities ?? []) {
            if (
              capability.provider_type === InternalAccountCapability.ProviderTypeEnum.TSYS ||
              capability.provider_type === InternalAccountCapability.ProviderTypeEnum.VALOR
            ) {
              hasTsys = true;
              break;
            }
          }

          return this._internalService
            .internalMerchantOrPartnerHasFeature({
              tilledAccount: account.id,
              id: 'elapp.integration.enabled',
            })
            .pipe(
              map((hasFeature) => ({ hasFeature, hasTsys })),
              catchError(() => of({ hasFeature: false, hasTsys })),
            );
        }),
      )
      .subscribe(({ hasFeature, hasTsys }) => {
        this._hasTsysProvider$.next(hasTsys && hasFeature);
      });
  }
}
